import {useFormik} from 'formik'
import {isEmpty} from 'lodash'
import React, {useCallback, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import {StorageHelpers} from '../../../../../../../_gori/helpers'
import useCancelToken from '../../../../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../../../../_gori/hooks/UseYupValidate'
import {Button, ButtonBack, InputTextFormik} from '../../../../../../../_gori/partials/widgets'
import {OrdersConfig} from '../../../../../orders/core/_const'
import {FromOrToModal} from '../../../../../orders'
import SettingsService from '../../../../core/_requests'
import {StoresListing} from '../StoresListing'
import {Api2CartConnectionConfig} from './config'
import {KTSVG} from '../../../../../../../_gori/helpers'
import {useNavigate} from 'react-router-dom'

type Props = {
  config: Api2CartConnectionConfig
}

const Api2CartConnectionPage: React.FC<Props> = ({config}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const [isReload, setIsReload] = useState(false)
  const navigate = useNavigate()
  const [showShipFromModal, setShowShipFromModal] = useState(false)
  const {stringYup, orderYup, regexYup, infoYup} = UseYupValidate()

  const translatedFields = useMemo(
    () =>
      config.fields.map((field) => ({
        ...field,
        translatedLabel: `${config.platformLabel} ${intl.formatMessage({id: field.label})}`,
      })),
    [config.fields, config.platformLabel, intl]
  )

  const validationSchema = useMemo(() => {
    const shape = translatedFields.reduce<Record<string, Yup.StringSchema>>((acc, field) => {
      let validator = Yup.string()
        .trim()
        .max(
          field.maxLength ?? 255,
          intl.formatMessage(
            {id: 'INPUT_MUST_NOT_BE_GREATER_THAN_CHARACTERS'},
            {input: field.translatedLabel, max: field.maxLength ?? 255}
          )
        )

      if (field.required) {
        validator = validator.required(
          intl.formatMessage({id: 'INPUT_IS_REQUIRED'}, {input: field.translatedLabel})
        )
      }

      if (field.type === 'url') {
        validator = validator.url(intl.formatMessage({id: 'PLEASE_ENTER_VALID_URL'}))
      }

      acc[field.name] = validator
      return acc
    }, {})

    return Yup.object().shape(shape)
  }, [intl, translatedFields])

  const shipFromInitialValues = useMemo(() => {
    if (!config.requireShipFrom) return {}

    return Object.entries(OrdersConfig.SHIPPING).reduce<Record<string, any>>(
      (initValue, [, value]) => {
        switch (value.value) {
          case OrdersConfig.SHIPPING.RESIDENTIAL.value:
            initValue[`from_${value.value}`] = false
            break
          case OrdersConfig.SHIPPING.VERIFIED.value:
          case OrdersConfig.SHIPPING.SAVE_ADDRESS.value:
            break
          default:
            initValue[`from_${value.value}`] = ''
            break
        }
        return initValue
      },
      {}
    )
  }, [config.requireShipFrom])

  const initialValues = useMemo(
    () => ({
      store_type: config.storeType,
      ...translatedFields.reduce<Record<string, string>>((acc, field) => {
        acc[field.name] = ''
        return acc
      }, {}),
      ...(config.requireShipFrom ? {from: shipFromInitialValues} : {}),
    }),
    [config.storeType, translatedFields, config.requireShipFrom, shipFromInitialValues]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting, resetForm}) => {
      setStatus('')

      try {
        await SettingsService.connect(values, {cancelToken: newCancelToken()})
        StorageHelpers.setItemLocalStorage('cardstore', {hidden: true})
        window.dispatchEvent(new Event('storage'))
        toast.success(intl.formatMessage({id: 'CONNECTION_SUCCESSFUL'}))
        resetForm()
        setIsReload(true)
      } catch (error: any) {
        if (isCancel(error)) return

        const message = error?.response?.data?.error?.message ?? ''
        setStatus(isEmpty(message) ? intl.formatMessage({id: 'CONNECTION_FAILED'}) : message)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const validateAddress = useCallback(
    (name: string) => {
      return {
        [`${name}_company`]: Yup.string().when([`${name}_first_name`], {
          is: (first_name) => !first_name || first_name.trim() === '',
          then: orderYup.companyFirstName('COMPANY'),
          otherwise: stringYup(255, 'COMPANY', false),
        }),
        [`${name}_first_name`]: Yup.string().when(`${name}_company`, {
          is: (company) => !company || company.trim() === '',
          then: orderYup.companyFirstName('FIRST_NAME'),
          otherwise: stringYup(255, 'FIRST_NAME', false),
        }),
        [`${name}_street1`]: stringYup(255, 'ADDRESS'),
        [`${name}_city`]: stringYup(255, 'CITY'),
        [`${name}_state`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.state(),
          otherwise: stringYup(255, 'STATE'),
        }),
        [`${name}_country`]: regexYup.country(),
        [`${name}_zip`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.zipCode(),
          otherwise: stringYup(255, 'POSTAL_CODE'),
        }),
        [`${name}_last_name`]: stringYup(255, 'LAST_NAME', false),
        [`${name}_street2`]: stringYup(255, 'ADDRESS_2', false),
        [`${name}_phone`]: infoYup.phone('PHONE', false),
        [`${name}_email`]: infoYup.email('EMAIL', false),
      }
    },
    [orderYup, regexYup, stringYup, infoYup]
  )

  const handleConnectClick = () => {
    if (config.requireShipFrom) {
      setShowShipFromModal(true)
    } else {
      formik.submitForm()
    }
  }

  const handleShipFromSave = (name: string, data: any) => {
    formik.setValues({...formik.values, ...data})
    formik.submitForm()
    setShowShipFromModal(false)
  }

  return (
    <>
      {config.requireShipFrom && showShipFromModal && (
        <FromOrToModal
          data={{
            show: showShipFromModal,
            name: 'from',
            data: formik.getFieldProps('from').value ?? shipFromInitialValues,
            validate: validateAddress('from'),
          }}
          labelModal={config.platformLabel + ' ' + intl.formatMessage({id: 'SHIP_FROM_LOCATION'})}
          handleSave={handleShipFromSave}
          handleClose={() => setShowShipFromModal(false)}
        />
      )}

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='store-connect-page'>
          <div className='store-connect-page__header'>
            <div className='store-connect-page__header-main'>
              <div className='d-flex align-items-center store-connect-page__back'>
                <ButtonBack className='store-connect-page__back-button me-0' />
                <button
                  type='button'
                  className='store-connect-page__back-arrow'
                  aria-label='Go back'
                  onClick={() => navigate(-1)}
                >
                  <KTSVG path='/media/gori/left_arrow.svg' />
                </button>
              </div>
              <div className='d-flex flex-column'>
                <div className='d-flex gap-4 align-items-center flex-wrap'>
                  <span className='store-connect-page__title fw-bolder text-dark fs-3'>
                    {intl.formatMessage({id: 'CONNECT_TO'})} {config.platformLabel}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className='store-connect-page__body'>
            <div className='store-connect-page__form-card store-connect-page__form-card--stacked'>
              <div className='store-connect-page__form-stack'>
                {translatedFields.map((field) => (
                  <div
                    key={field.name}
                    className='row align-items-center store-connect-page__form-stack-row'
                  >
                    <InputTextFormik
                      className='store-connect-page__form-stack-input col-10'
                      labelClassName='store-connect-page__form-stack-label col-2 col-form-label text-nowrap'
                      required={field.required}
                      label={field.translatedLabel}
                      formik={formik}
                      name={field.name}
                    />
                  </div>
                ))}

                {formik.status && (
                  <div className='col-md-12 mt-5 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>{formik.status}</div>
                  </div>
                )}

                <div className='store-connect-page__form-actions text-end'>
                  <Button
                    className='btn btn-advanced-primary mt-7 store-connect-page__form-stack-connect'
                    label={intl.formatMessage({id: 'CONNECT'})}
                    loadingText={intl.formatMessage({id: 'CONNECT'})}
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting || !formik.isValid}
                    event={handleConnectClick}
                  />
                </div>
              </div>
            </div>

            <div className='store-connect-page__stores'>
              <StoresListing
                isReload={isReload}
                setIsReload={setIsReload}
                storeType={config.storeType}
              />
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}

export {Api2CartConnectionPage}
